import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFunctionContext } from "../../../context/FunctionalContext";
import {
  Home,
  Image as ImageIcon,
  Ruler,
  Bed,
  Bath,
  Landmark,
  Building,
  MapPin,
  Globe,
  CalendarDays,
  Tag,
  LocateFixed,
  LandmarkIcon,
  Layers,
  Eye,
  Building2,
} from "lucide-react";
import RealtyNewsLetter from "../common/RealtyNewsLetter";

const ListingView = () => {
  const { id } = useParams();
  const { listings } = useFunctionContext();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);

    return () => clearTimeout(timer);
  });

  const eachListing = listings.find((l) => l.listing_id === id);

  if (!eachListing || !showContent)
    return (
      <div className="max-w-6xl mx-auto py-6 md:py-16 my-24 px-6">
        <div className="animate-pulse max-w-6xl mx-auto px-4 py-6">
          <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-2/3 mb-4"></div>

          <div className="relative w-full h-[300px] bg-gray-800 rounded-lg mb-8"></div>

          <div className="space-y-3 mb-10">
            <div className="h-4 bg-gray-700 rounded w-full"></div>
            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
            <div className="h-4 bg-gray-700 rounded w-2/3"></div>
          </div>

          <div className="relative w-full h-[200px] bg-gray-800 rounded-lg mb-8"></div>

          {[1, 2, 3, 4, 5, 6].map((_, idx) => (
            <div key={idx} className="mb-10">
              {/* Section Title */}
              <div className="h-5 bg-gray-600 rounded w-2/3 mb-4"></div>

              <div className="space-y-3">
                {[1, 2, 3, 4].map((_, bIdx) => (
                  <div key={bIdx} className="flex items-start space-x-2">
                    <div className="w-2 h-2 mt-2 rounded-full bg-gray-500"></div>
                    <div className="h-4 bg-gray-700 rounded w-full"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Final Tip Block */}
          <div className="h-5 bg-gray-600 rounded w-1/2 mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
            <div className="h-4 bg-gray-700 rounded w-2/3"></div>
            <div className="h-4 bg-gray-700 rounded w-4/5"></div>
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );

  function convertTextWithLinks(text) {
    if (!text) return "";

    // Regex to match plain URLs that are not inside an existing <a> tag
    const urlRegex = /(?<!href=")(https?:\/\/[^\s"<]+)/g;

    // Split into paragraphs
    const paragraphs = text.split(/\n+/).filter((p) => p.trim() !== "");

    // Convert URLs to <a> tags safely
    const htmlParagraphs = paragraphs.map((para) => {
      return `<p>${para.replace(urlRegex, (url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      })}</p>`;
    });

    return htmlParagraphs.join("");
  }

  return (
    <div className="max-w-6xl px-6 mx-auto py-6 md:py-16 my-24 lg:mt-36 rounded-2xl bg-slate-100 text-gray-900">
      {/* Title */}
      <div className="mb-10 space-y-3">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold flex items-start gap-3 capitalize">
          <Home className="text-gold shrink-0" size={45} />{" "}
          {eachListing.name || "Unnamed Property"}
        </h2>
        <p className="text-lg text-gray-40 mt-2 capitalize flex items-center gap-2">
          <Tag size={18} className="text-gray-40" />
          <strong className="underline font-extrabold">Type:</strong>{" "}
          {eachListing.type || "None"}
        </p>
        <p className="text-sm text-gray-5 flex items-center gap-2 mt-1">
          <CalendarDays size={16} />{" "}
          {eachListing.updatedAt
            ? new Date(eachListing.updatedAt).toLocaleDateString()
            : "None"}
        </p>
      </div>

      {/* Images */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-8 mb-10">
        {eachListing.images?.length > 0 ? (
          eachListing.images.map((img, i) => (
            <img
              key={i}
              src={img.url}
              alt={`Image ${i + 1}`}
              className="w-full h-[400px] object-contain borde border-slate-100/1 shadow-md hover:scale-105 transition-transform duration-300"
            />
          ))
        ) : (
          <div className="col-span-2 text-center text-gray-5 py-5 bg-zinc-900/80 backdrop-blur-lg rounded-lg">
            <ImageIcon className="mx-auto mb-2" size={40} />
            No images available
          </div>
        )}
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10 text-gray-20 items-start">
        <div className="flex items-start gap-2 flex-col ">
          <div className="inline-flex gap-1 items-center">
            <Building2 size={20} />
            <strong className="underline font-extrabold">
              Amenities:
            </strong>{" "}
          </div>
          <ul className="ps-4 text-sm">
            {eachListing.amenities?.length > 0
              ? eachListing.amenities.map((amenity, idx) => (
                  <li key={idx} className="list-disc">
                    {amenity.name}
                    {/* {""}({amenity.count}) */}
                    {idx < eachListing.amenities.length - 1 && ", "}
                  </li>
                ))
              : "None"}
          </ul>
        </div>
        <div className="flex items-center gap-2">
          <Ruler size={20} />
          <span>
            <strong className="underline font-extrabold">Area:</strong>{" "}
            {eachListing.area_sqft || "None"} sqft
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Bed size={20} />
          <span>
            <strong className="underline font-extrabold">Bedrooms:</strong>{" "}
            {eachListing.bedrooms ?? "None"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Bath size={20} />
          <span>
            <strong className="underline font-extrabold">Bathrooms:</strong>{" "}
            {eachListing.bathrooms ?? "None"}
          </span>
        </div>
        {/* <div className="flex items-center gap-2">
          <LandmarkIcon size={20} />
          <span>
            <strong className="underline font-extrabold">Balconies:</strong>{" "}
            {eachListing.balconies ?? "None"}
          </span>
        </div> */}
        <div className="flex items-center gap-2">
          <Tag size={20} />
          <span>
            {/* currency_symbol */}
            <strong className="underline font-extrabold">Price:</strong>{" "}
            {eachListing.price?.toLocaleString() || "None"}{" "}
            {eachListing.currency_symbol}
          </span>
        </div>
      </div>

      {/* Description */}
      <div className="mb-10">
        <h4 className="text-2xl font-semibold mb-2 flex items-center gap-2">
          <Eye size={22} />
          Description
        </h4>

        {eachListing.description ? (
          <div
            className="description-content text-gray-30 [&>p]:mb-4 [&>p>a]:text-blue-500 [&>p>a]:underline"
            dangerouslySetInnerHTML={{
              __html: convertTextWithLinks(eachListing.description),
            }}
          />
        ) : (
          <p className="text-gray-30">No description available.</p>
        )}
      </div>

      {/* Location Info */}
      <div>
        <h4 className="text-2xl font-semibold mb-3 flex items-center gap-2">
          <MapPin size={22} />
          Location Details
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10 text-gray-20">
          <div className="flex flex-col items-start">
            <strong className="underline font-extrabold">Address:</strong>{" "}
            {eachListing?.location?.address || "None"}
          </div>
          <div className="flex flex-col items-start">
            <strong className="underline font-extrabold">City:</strong>{" "}
            {eachListing?.location?.city || "None"}
          </div>
          {/* <div className="flex flex-col items-start">
            <strong className="underline font-extrabold">State:</strong>{" "}
            {eachListing?.location?.state || "None"}
          </div> */}
          <div className="flex flex-col items-start">
            <strong className="underline font-extrabold">Country:</strong>{" "}
            {eachListing?.location?.country || "None"}
          </div>
          {/* <div className="flex flex-col items-start">
            <strong className="underline font-extrabold">Pincode:</strong>{" "}
            {eachListing?.location?.pincode || "None"}
          </div> */}
          <div className="flex flex-col items-start">
            <strong className="underline font-extrabold">Landmark:</strong>{" "}
            {eachListing?.location?.landmark || "None"}
          </div>
          <div className="flex flex-col items-start">
            <RealtyNewsLetter />
            {/* customClass="!text-base !px-2.5 !py-1" */}
          </div>
        </div>
        {/* <ul className="text-gray-300 space-y-3 lg:space-y-0 lg:space-x-28 flex flex-col lg:flex-row items-start ">
          <li className="flex flex-col items-start">
            <strong className="underline font-extrabold">Address:</strong>{" "}
            {eachListing?.location?.address || "None"}
          </li>
          <li className="flex flex-col items-start">
            <strong className="underline font-extrabold">City:</strong>{" "}
            {eachListing?.location?.city || "None"}
          </li>
          <li className="flex flex-col items-start">
            <strong className="text-gold">State:</strong>{" "}
            {eachListing?.location?.state || "None"}
          </li>
          <li className="flex flex-col items-start">
            <strong className="text-gold">Country:</strong>{" "}
            {eachListing?.location?.country || "None"}
          </li>
          <li className="flex flex-col items-start">
            <strong className="text-gold">Pincode:</strong>{" "}
            {eachListing?.location?.pincode || "None"}
          </li>
          <li className="flex flex-col items-start">
            <strong className="text-gold">Landmark:</strong>{" "}
            {eachListing?.location?.landmark || "None"}
          </li>
        </ul> */}
      </div>
    </div>
  );
};

export default ListingView;
